import type { ReactNode, SyntheticEvent } from 'react';
import React, { useState } from 'react';
import styled from 'styled-components';
import type { HeadingTags } from 'lib/design-system';
import { BodyTextSmall } from 'lib/design-system';
import {
  colorBorderDecorative,
  HeadingXS,
  IconChevronUp,
  lineHeight2,
} from 'lib/design-system';
import {
  default500,
  fontSizeLabelsRegular,
  iconSizeSmall,
  neutral600,
  neutral900,
  spacing2,
  spacing4,
} from 'lib/mortar';

const List = styled.ul<StyledUnorderedList>`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const ListItem = styled.li<StyledListItem>`
  border-bottom: 1px solid ${colorBorderDecorative};

  &:first-child {
    border-top: 1px solid ${colorBorderDecorative};
  }
`;

const Summary = styled.summary`
  display: flex; /* Instead of list-item. This removes the left-hand marker */
  gap: ${spacing4};
  cursor: pointer;

  &::-webkit-details-marker {
    display: none;
  }

  ${HeadingXS} {
    color: ${neutral900};
  }
`;
const ChevronContainer = styled.span<StyledSpan>`
  max-height: ${iconSizeSmall};
  margin-top: calc((${lineHeight2} - ${iconSizeSmall}) / 2);
  transition: transform 0.3s;
  transform: rotate(180deg);
`;

const Details = styled.details`
  position: relative;
  padding: ${spacing4} 0;

  &[open] {
    ${ChevronContainer} {
      transform: rotate(360deg);
    }
  }
`;

const TextWrapper = styled.div<StyledDiv>`
  width: 100%;

  ${HeadingXS} {
    display: inline;
    color: ${neutral900};
  }

  ${BodyTextSmall} {
    font-size: ${fontSizeLabelsRegular};
    display: inline;
    margin-left: ${spacing2};
    color: ${neutral600};
  }
`;

const HeadingWrapper = styled(TextWrapper)`
  padding: ${spacing4} 0;
`;

interface Item {
  name: string;
  heading: string;
  child: ReactNode;
  open?: boolean;
  subtitle?: string;
}

interface AccordionProps {
  items: Item[];
  // eslint-disable-next-line react/require-default-props
  onClick?: (event: SyntheticEvent, index: number) => void;
  // eslint-disable-next-line react/require-default-props
  className?: string;
  headingLevels: HeadingTags;
  iconPosition?: 'left' | 'right';
  chevronColor?: string;
}

// Totally copied from https://github.com/votemike/react-accordion/blob/main/src/index.tsx (but it only supports React 18.2.0+)
export function Accordion(props: AccordionProps) {
  const {
    className,
    items,
    onClick,
    headingLevels,
    iconPosition = 'right',
    chevronColor = default500,
  } = props;

  return (
    <List className={className}>
      {items.map((item, index) => (
        <ListItem key={item.name}>
          {item.child ? (
            <Details as="details" aria-label={item.name} open={item.open}>
              <Summary
                as="summary"
                onClick={(event: SyntheticEvent) =>
                  typeof onClick === 'function' && onClick(event, index)
                }
              >
                {iconPosition === 'left' && (
                  <ChevronContainer>
                    <IconChevronUp size="small" color={chevronColor} />
                  </ChevronContainer>
                )}
                <TextWrapper>
                  <HeadingXS as={headingLevels}>{item.heading}</HeadingXS>
                  {item.subtitle && (
                    <BodyTextSmall>{item.subtitle}</BodyTextSmall>
                  )}
                </TextWrapper>
                {iconPosition === 'right' && (
                  <ChevronContainer>
                    <IconChevronUp size="small" color={chevronColor} />
                  </ChevronContainer>
                )}
              </Summary>
              {item.child}
            </Details>
          ) : (
            <HeadingWrapper>
              <HeadingXS as={headingLevels}>{item.heading}</HeadingXS>
            </HeadingWrapper>
          )}
        </ListItem>
      ))}
    </List>
  );
}

export const SingleItemOpenAccordion = ({
  headingLevels,
  items,
  iconPosition = 'right',
  chevronColor,
  onOpenChange,
}: {
  headingLevels: HeadingTags;
  items: Item[];
  iconPosition?: 'left' | 'right';
  chevronColor?: string;
  onOpenChange?: (isOpen: boolean, index: number) => void;
}) => {
  let initialOpenItem = null;
  const reversedOpenItemIndex = items
    .slice()
    .reverse()
    .findIndex(item => item.open);
  if (reversedOpenItemIndex !== -1) {
    initialOpenItem = items.length - reversedOpenItemIndex - 1;
  }

  const [openItemIndex, setOpenItemIndex] = useState<number | null>(
    initialOpenItem,
  );

  const handleClick = (event: SyntheticEvent, index: number) => {
    event.preventDefault();
    const newIndex = openItemIndex === index ? null : index;
    setOpenItemIndex(newIndex);

    if (onOpenChange) {
      onOpenChange(newIndex !== null, index);
    }
  };

  const revisedItems = items.map((item, index) => ({
    ...item,
    open: index === openItemIndex,
  }));

  return (
    <Accordion
      items={revisedItems}
      onClick={handleClick}
      headingLevels={headingLevels}
      iconPosition={iconPosition}
      chevronColor={chevronColor}
    />
  );
};
